import { useEffect, useState } from 'react';
import { Icon } from '@/modules/Icon';
import { Button } from '@/modules/Button';
import { EmojiPicker } from '@/modules/EmojiPicker';
import { Separator } from '@/modules/Separator';
import { WysiwygProps } from '@/shared/types/wysiwyg';
import { useWysiwyg } from '../../hooks/useWysiwyg';
import { cn } from '@/shared/utils/cn';
import {
  faLink,
  faListOl,
  faListUl,
  faPlus,
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faPaperPlane,
  faAt,
  faShapes,
} from '@fortawesome/pro-regular-svg-icons';

// Componente auxiliar para botones de formato usando Button
interface FormatButtonProps {
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: typeof faBold;
  tooltip: string;
}

const FormatButton = ({ isActive, onClick, disabled, icon, tooltip }: FormatButtonProps) => (
  <Button
    color={isActive ? 'primary' : 'secondary'}
    size="xxs"
    icon={icon}
    onClick={onClick}
    disabled={disabled}
    tooltip={tooltip}
    pill="xs"
    className={cn('ui-h-8 ui-w-8')}
  />
);

export const Wysiwyg = ({
  value,
  defaultValue = '',
  onChange,
  placeholder = 'Escribe un mensaje nuevo',
  disabled = false,
  showToolbar = true,
  allowAttachments = true,
  onAttachment,
  allowMentions = true,
  onMention,
  showSubmitButton = true,
  onSubmit,
  submitButtonText,
  className = '',
  autoFocus = false,
  minHeight = 60,
  maxHeight = 300,
}: WysiwygProps) => {
  const {
    editorRef,
    isFocused,
    setIsFocused,
    activeFormats,
    execCommand,
    handleEditorClick,
    insertEmoji,
    getHTML,
    setHTML,
    isEmpty,
    updateActiveFormats,
  } = useWysiwyg();

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  // Sincronizar valor externo
  useEffect(() => {
    if (value !== undefined && editorRef.current) {
      if (getHTML() !== value) {
        setHTML(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editorRef es ref, no dependencia
  }, [value, getHTML, setHTML]);

  // Establecer valor por defecto
  useEffect(() => {
    if (defaultValue && editorRef.current && !editorRef.current.innerHTML) {
      setHTML(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editorRef es ref, no dependencia
  }, [defaultValue, setHTML]);

  // Auto focus
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editorRef es ref, no dependencia
  }, [autoFocus]);

  // Manejar cambios en el contenido
  const handleInput = () => {
    const html = getHTML();
    onChange?.(html);
    setIsSubmitDisabled(isEmpty());
  };

  // Manejar envío
  const handleSubmit = () => {
    if (!isEmpty() && !disabled) {
      onSubmit?.();
    }
  };

  // Manejar adjuntar archivo
  const handleAttachFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        onAttachment?.(file);
      }
    };
    input.click();
  };

  // Verificar si un formato está activo
  const isFormatActive = (format: string) => activeFormats.has(format);

  return (
    <div
      className={cn(
        'ui-imj',
        'ui-w-full ui-rounded-control-xs ui-border-1 ui-border-default ui-bg-surface-default ui-transition-colors',
        'ui-border-default',
        isFocused && 'ui-border-default',
        disabled && 'ui-pointer-events-none ui-opacity-50',
        className,
      )}
    >
      <div className="ui-flex ui-w-full ui-flex-col ui-gap-4 ui-px-8 ui-py-6">
        {/* Toolbar de formato */}
        {showToolbar && (
          <div
            className="ui-flex ui-w-full ui-items-center ui-gap-4 ui-rounded-control-xs ui-bg-surface-secondary ui-p-4"
            onMouseDown={(e) => e.preventDefault()}
          >
            {/* Bold */}
            <FormatButton
              isActive={isFormatActive('bold')}
              onClick={() => execCommand('bold')}
              disabled={disabled}
              icon={faBold}
              tooltip="Negrita"
            />

            {/* Italic */}
            <FormatButton
              isActive={isFormatActive('italic')}
              onClick={() => execCommand('italic')}
              disabled={disabled}
              icon={faItalic}
              tooltip="Cursiva"
            />

            {/* Underline */}
            <FormatButton
              isActive={isFormatActive('underline')}
              onClick={() => execCommand('underline')}
              disabled={disabled}
              icon={faUnderline}
              tooltip="Subrayado"
            />

            {/* Strikethrough */}
            <FormatButton
              isActive={isFormatActive('strikethrough')}
              onClick={() => execCommand('strikeThrough')}
              disabled={disabled}
              icon={faStrikethrough}
              tooltip="Tachado"
            />

            {/* Divider */}
            <Separator vertical className="ui-h-12" />

            {/* Unordered List */}
            <FormatButton
              isActive={isFormatActive('ul')}
              onClick={() => execCommand('insertUnorderedList')}
              disabled={disabled}
              icon={faListUl}
              tooltip="Lista sin orden"
            />

            {/* Ordered List */}
            <FormatButton
              isActive={isFormatActive('ol')}
              onClick={() => execCommand('insertOrderedList')}
              disabled={disabled}
              icon={faListOl}
              tooltip="Lista ordenada"
            />

            {/* Divider */}
            <Separator vertical className="ui-h-12" />

            {/* Link */}
            <FormatButton
              isActive={false}
              onClick={() => {
                const url = prompt('Ingresa la URL:');
                if (url) {
                  execCommand('createLink', url);
                }
              }}
              disabled={disabled}
              icon={faLink}
              tooltip="Insertar enlace"
            />
          </div>
        )}

        {/* Editor de contenido */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          className={cn(
            'ui-w-full ui-overflow-y-auto ui-px-8 ui-py-6 ui-text-body-sm-regular ui-font-body-sm-regular ui-text-primary ui-outline-none',
            'focus:ui-outline-none',
            '[&:empty]:before:ui-text-body-sm-regular [&:empty]:before:ui-font-body-sm-regular [&:empty]:before:ui-text-tertiary [&:empty]:before:ui-content-[attr(data-placeholder)]',
            // Estilos para listas
            '[&_ul]:ui-my-2 [&_ul]:ui-list-disc [&_ul]:ui-pl-24',
            '[&_ol]:ui-my-2 [&_ol]:ui-list-decimal [&_ol]:ui-pl-24',
            '[&_li]:ui-my-1',
          )}
          data-placeholder={placeholder}
          onInput={handleInput}
          onFocus={() => {
            setIsFocused(true);
            updateActiveFormats();
          }}
          onBlur={() => {
            setIsFocused(false);
            // No limpiar formatos activos al perder focus:
            // los botones del toolbar mantienen su estado
          }}
          onClick={handleEditorClick}
          style={{
            minHeight: `${minHeight}px`,
            maxHeight: `${maxHeight}px`,
          }}
        />

        {/* Barra de acciones inferior */}
        <div
          className="ui-flex ui-w-full ui-items-center ui-justify-between"
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Acciones izquierda */}
          <div className="ui-flex ui-items-center ui-gap-8">
            {/* Botón adjuntar */}
            {allowAttachments && (
              <>
                <Button
                  color="tertiary"
                  size="xs"
                  icon={faShapes}
                  onClick={handleAttachFile}
                  disabled={disabled}
                  tooltip="Adjuntar archivo"
                />
                <Separator vertical className="ui-h-12" />
              </>
            )}

            {/* Emoji Picker */}
            <EmojiPicker
              color="tertiary"
              size="xs"
              position="bottom-left"
              onEmojiSelect={insertEmoji}
              closeOnSelect={true}
              width={330}
              height={300}
            />

            {/* Menciones */}
            {allowMentions && (
              <Button
                color="tertiary"
                size="xs"
                icon={faAt}
                onClick={() => {
                  const mention = prompt('Mencionar a:');
                  if (mention) {
                    insertEmoji(`@${mention} `);
                    onMention?.(mention);
                  }
                }}
                disabled={disabled}
                tooltip="Mencionar"
              />
            )}
          </div>

          {/* Botón enviar */}
          {showSubmitButton && (
            <Button
              color={isSubmitDisabled || disabled ? 'secondary' : 'primary'}
              size="sm"
              icon={faPaperPlane}
              onClick={handleSubmit}
              disabled={disabled || isSubmitDisabled}
              tooltip={submitButtonText || 'Enviar'}
            />
          )}
        </div>
      </div>
    </div>
  );
};
